import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Fish, Phone, Mail, MapPin, Facebook, Instagram, Youtube } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { SITE_CONFIG } from "@/lib/constants";

interface FooterSettings {
  phone: string;
  email: string;
  whatsapp: string;
  hours: string;
  facebook: string;
  instagram: string;
  youtube: string;
  location_1_name: string;
  location_1_address: string;
  location_2_name: string;
  location_2_address: string;
}

const DEFAULT_SETTINGS: FooterSettings = {
  phone: SITE_CONFIG.contact.phone,
  email: SITE_CONFIG.contact.email,
  whatsapp: SITE_CONFIG.contact.whatsapp,
  hours: "Mon - Sat, 9am - 6pm",
  facebook: SITE_CONFIG.socialLinks.facebook,
  instagram: SITE_CONFIG.socialLinks.instagram,
  youtube: SITE_CONFIG.socialLinks.youtube,
  location_1_name: SITE_CONFIG.locations[0]?.name || "",
  location_1_address: SITE_CONFIG.locations[0]?.address || "",
  location_2_name: SITE_CONFIG.locations[1]?.name || "",
  location_2_address: SITE_CONFIG.locations[1]?.address || "",
};

export function Footer() {
  const [settings, setSettings] = useState<FooterSettings>(DEFAULT_SETTINGS);

  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("key, value")
        .like("key", "footer_%");

      if (data && data.length > 0) {
        const s = { ...DEFAULT_SETTINGS };
        data.forEach((item) => {
          const k = item.key.replace("footer_", "") as keyof FooterSettings;
          if (k in s && item.value) {
            s[k] = item.value;
          }
        });
        setSettings(s);
      }
    };
    fetchSettings();
  }, []);

  const locations = [
    { name: settings.location_1_name, address: settings.location_1_address },
    { name: settings.location_2_name, address: settings.location_2_address },
  ].filter((l) => l.name);

  return (
    <footer className="bg-card border-t border-border">
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-2">
              <Fish className="h-8 w-8 text-primary" />
              <span className="font-serif text-xl font-bold text-gradient">
                Oyanishikigoi
              </span>
            </Link>
            <p className="text-muted-foreground text-sm">
              {SITE_CONFIG.description}
            </p>
            <div className="flex items-center gap-3">
              {settings.facebook && (
                <a href={settings.facebook} target="_blank" rel="noopener noreferrer" aria-label="Follow Oyanishikigoi on Facebook" className="p-2 rounded-lg bg-muted hover:bg-primary/20 hover:text-primary transition-colors">
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {settings.instagram && (
                <a href={settings.instagram} target="_blank" rel="noopener noreferrer" aria-label="Follow Oyanishikigoi on Instagram" className="p-2 rounded-lg bg-muted hover:bg-primary/20 hover:text-primary transition-colors">
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {settings.youtube && (
                <a href={settings.youtube} target="_blank" rel="noopener noreferrer" aria-label="Watch Oyanishikigoi on YouTube" className="p-2 rounded-lg bg-muted hover:bg-primary/20 hover:text-primary transition-colors">
                  <Youtube className="h-5 w-5" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {[
                { href: "/shop", label: "Shop All" },
                { href: "/shop?category=nishikigoi", label: "Nishikigoi" },
                { href: "/shop?category=supplies", label: "Aquarium Supplies" },
                { href: "/b2b", label: "B2B / Export" },
                { href: "/services", label: "Services" },
              ].map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-muted-foreground hover:text-primary transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Customer Service */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-4">Customer Service</h4>
            <ul className="space-y-2">
              {[
                { href: "/about", label: "About Us" },
                { href: "/contact", label: "Contact" },
                { href: "/faq", label: "FAQ" },
                { href: "/shipping", label: "Shipping Info" },
                { href: "/returns", label: "Returns Policy" },
              ].map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-muted-foreground hover:text-primary transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-3">
              {settings.phone && (
                <li className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">{settings.phone}</p>
                    {settings.hours && <p className="text-xs text-muted-foreground">{settings.hours}</p>}
                  </div>
                </li>
              )}
              {settings.email && (
                <li className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-primary mt-0.5" />
                  <a href={`mailto:${settings.email}`} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    {settings.email}
                  </a>
                </li>
              )}
              {locations.map((location) => (
                <li key={location.name} className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">{location.name}</p>
                    <p className="text-xs text-muted-foreground">{location.address}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-border mt-12 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Oyanishikigoi. All rights reserved. | Site Developed by{" "}
            <a href="https://www.zligger.com" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-400 transition-colors font-medium">
              Zligger
            </a>
          </p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
